import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Container } from '@mui/material';
import { CustomBreadcrumbsProps } from './types';
import LinkItem from './link-item';

export default function CustomBreadcrumbs({
  links,
  action,
  heading,
  moreLink,
  activeLast,
  sx,
  ...other
}: CustomBreadcrumbsProps) {
  const lastLink = links.length > 0 ? links[links.length - 1]?.name : '';

  return (
    <Box sx={{ ...sx }}>
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 1, sm: 1.5, md: 2 },
        }}
      >
        {/* UNIFIED LAYOUT - SAME FOR MOBILE AND DESKTOP */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          {/* LEFT SIDE - HEADING AND BREADCRUMBS */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* HEADING */}
            {heading && (
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontSize: { xs: '1.5rem', sm: '1.6rem' },
                  wordBreak: 'break-word',
                  mb: 0.5,
                }}
              >
                {heading}
              </Typography>
            )}

            {/* BREADCRUMBS - Below title */}
            {!!links.length && (
              <Box
                sx={{
                  overflow: 'hidden',
                  '& .MuiBreadcrumbs-root': {
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                  },
                  '& .MuiBreadcrumbs-ol': {
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    gap: { xs: 0.5, sm: 1 },
                  },
                }}
              >
                <Breadcrumbs
                  separator={<Separator />}
                  {...other}
                  sx={{
                    '& .MuiBreadcrumbs-separator': {
                      mx: { xs: 0.5, sm: 1 },
                    },
                  }}
                >
                  {links.map((link) => (
                    <LinkItem
                      key={link.name || ''}
                      link={link}
                      activeLast={activeLast}
                      disabled={link.name === lastLink}
                    />
                  ))}
                </Breadcrumbs>
              </Box>
            )}
          </Box>

          {/* RIGHT SIDE - ACTION BUTTON (Centered vertically) */}
          {action && (
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                height:
                  heading && links.length
                    ? { xs: 'calc(1.5rem + 1.5rem + 8px)', sm: 'calc(2rem + 1.5rem + 8px)' }
                    : { xs: '1.5rem', sm: '2rem' },
                pt: heading ? '0.25rem' : 0,
              }}
            >
              {action}
            </Box>
          )}
        </Stack>

        {/* MORE LINK */}
        {!!moreLink && (
          <Box sx={{ mt: 2 }}>
            {moreLink.map((href) => (
              <Link
                key={href}
                href={href}
                variant="body2"
                target="_blank"
                rel="noopener"
                sx={{ display: 'table' }}
              >
                {href}
              </Link>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

function Separator() {
  return (
    <Box
      component="span"
      sx={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        bgcolor: 'text.disabled',
      }}
    />
  );
}
